/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2019-2024 (original work) Open Assessment Technologies SA
 */

import pageController from '@/controller/page';
import TenantLogin from '../pages/TenantLogin.svelte';
import config from '@/config';
import { configurationStore } from '@/stores/tenantConfiguration.store.js';
import { __ } from '@oat-sa-private/ui-core';
import { showNotification } from '@oat-sa-private/ui-components';
import * as authService from '@/services/authService';
import * as mfaAuthService from '@/services/mfaAuthService';
import { log, locale as utilsLocale } from '@/core/utils';
import { user } from '@/stores/auth.store';
import { getError } from './utils';
import { ERROR_MESSAGES } from './loginErrorMessages';
import { logActivity } from '@/services/activityLogService';

export default () =>
    pageController({
        name: 'tenant_login',

        /**
         * TenantLogin page controller entrypoint
         * @param {Object} params - URL query params
         * @param {String} [params.refreshToken] - can be used to bypass the login form and log in automatically (e.g. from SSO redirect)
         * @param {String} [params.targetUrl] - can be used to redirect to another page after login. Must be an absolute URL
         * @param {Object} pathParams - URL path parts which matched a route
         * @param {String} [pathParams.locale]
         */
        async start(params = {}, pathParams = {}) {
            const requestedLocale = pathParams.locale;
            await utilsLocale.updateApplicationLocale(requestedLocale);

            this.setPageTitle(__('Login'));

            let error;
            let displayPreferredRoleModal; // See README for more info about preferred role

            // Will we be logged in automatically?
            const { refreshToken, targetUrl } = params;
            if (refreshToken) {
                try {
                    await authService.storeTokens({ refreshToken });
                    await user.updateInfo();
                    let shouldChooseRole = await authService.needsToChoosePreferredRole();
                    if (shouldChooseRole) {
                        displayPreferredRoleModal = true;
                    } else if (user.get() !== null) {
                        await utilsLocale.updateApplicationLocale();
                        this.router.redirect(targetUrl ? { fullUrl: targetUrl } : config.routes.home);
                        return;
                    } else {
                        // clean url params
                        this.router.replace(window.location.pathname, false);
                        throw new Error('could not log in with refreshToken');
                    }
                } catch (e) {
                    error = ERROR_MESSAGES.unexpectedError;
                    log.error(e);
                }
            }

            // Now we _will_ show the login form
            const tenantClientId = await getTenantClientId();
            const ssoProviders = configurationStore.get()?.ssoProviders || [];

            const showRemainingTimeNotification = remainingSeconds => {
                const seconds = remainingSeconds % 60;
                const minutes = Math.floor(remainingSeconds / 60);
                const remainingTime = `${minutes ? `${minutes} ${__('minutes')}` : ''} ${
                    minutes && seconds ? __('and') : ''
                } ${seconds ? `${seconds} ${__('seconds')}` : ''}`;

                showNotification({
                    title: __('You have reached the maximum number of login attempts'),
                    message: __('Please wait %d before trying to log in again', remainingTime),
                    hierarchy: 'warning'
                });
            };

            const loginHandler = async evt => {
                error = null;
                try {
                    await authService.login(evt.detail);
                    await user.updateInfo();
                    let shouldChooseRole = await authService.needsToChoosePreferredRole();
                    if (shouldChooseRole) {
                        // stay in the login screen and display the choose preferred role dialog. See README for more info about preferred role
                        this.component.$set({ displayPreferredRoleModal: true });
                    } else {
                        await this.continueLogin(targetUrl, ssoProviders, error);
                    }
                } catch (requestError) {
                    if (requestError?.response?.status === 429) {
                        const responseBody = await requestError?.response?.json();
                        showRemainingTimeNotification(responseBody.retryAfterSeconds);
                    }
                    error = await getError(requestError, !!ssoProviders.length);
                    log.error(requestError, await requestError.response?.json());
                }

                this.component.$set({ error });
            };

            if (params?.error === 'invalidCredentials' && !error) {
                error =
                    ssoProviders.length > 0
                        ? ERROR_MESSAGES.invalidCredentialsTrySSO
                        : ERROR_MESSAGES.invalidCredentials;
                log.error('Invalid credentials received', error);
            }

            this.mount(TenantLogin, {
                tenantClientId,
                ssoProviders,
                displayPreferredRoleModal,
                error
            });

            this.component.$on('login', loginHandler);

            this.component.$on('sso-login', async evt => {
                try {
                    await authService.ssoLogin(evt.detail);
                } catch (requestError) {
                    error = await getError(requestError, !!ssoProviders.length);
                    if (!error) {
                        error = ERROR_MESSAGES.unexpectedError;
                        log.error(requestError, await requestError.response?.json());
                    }
                    this.component.$set({ error });
                }
            });

            this.component.$on('anonymous-login', async () => {
                this.router.redirect(config.routes.publicSessions.sessions);
            });
            this.component.$on('chosePreferredRole', async ({ detail }) => {
                this.continueLogin(targetUrl, ssoProviders, error, detail?.preferredRole);
            });

            this.component.$on('mfa-login', async evt => {
                error = null;
                const { detail } = evt;
                try {
                    const response = await mfaAuthService.requestMfaCode(detail);

                    if (response?.status === 'NOT_REQUIRED') {
                        await loginHandler(evt);
                    }
                } catch (requestError) {
                    if (requestError?.response?.status === 429) {
                        const responseBody = await requestError?.response?.json();
                        showRemainingTimeNotification(responseBody.retryAfterSeconds);
                    }
                    error = await getError(requestError, !!ssoProviders.length);
                    log.error(requestError, await requestError.response?.json());
                }

                this.component.$set({ error });
            });

            this.component.$on('mfa-verify-code', async ({ detail }) => {
                error = null;
                try {
                    const token = await mfaAuthService.verifyMfaCode(detail);
                    this.router.redirect(`${config.routes.login.main}?refreshToken=${token}`);
                } catch (requestError) {
                    if (requestError?.response?.status === 429) {
                        const responseBody = await requestError?.response?.json();
                        showRemainingTimeNotification(responseBody.retryAfterSeconds);
                    }
                    error = await getError(requestError);
                    log.error(requestError, await requestError.response?.json());
                }

                this.component.$set({ error });
            });

            this.component.$on('mfa-cancel', () => {
                mfaAuthService.cancelMfa();
                this.router.redirect(config.routes.login.main);
            });
        },

        async continueLogin(targetUrl, ssoProviders, error, preferredRole) {
            try {
                if (preferredRole) {
                    authService.setPreferredRole(preferredRole);
                }
                await utilsLocale.updateApplicationLocale();

                if (window.history.state && window.history.state.backPath) {
                    return this.router.redirect(window.history.state.backPath);
                }

                this.router.redirect(targetUrl ? { fullUrl: targetUrl } : config.routes.home);
                await logActivity({ action: 'login' });
            } catch (requestError) {
                error = await getError(requestError, !!ssoProviders.length);
                if (!error) {
                    error = ERROR_MESSAGES.unexpectedError;
                    log.error(requestError, await requestError.response?.json());
                }
                this.component.$set({ error });
            }
        }
    });

/**
 * Gets the portalClientId from the environment manager (auth server).
 * @returns {String} Portal tenant clientId
 */
async function getTenantClientId() {
    try {
        const tenant = await authService.getTenant();
        if (tenant?.portalClientId) {
            return tenant.portalClientId;
        }
        throw Error(`Received subdomains object does not contain a portalClientId key`);
    } catch (e) {
        log.error(`Login page couldn't get portalClientId from API`, e);
    }
}
